import { ReviewBadge, useReviews } from "./Reviews";
import { useEffect, useRef, useState } from "react";
import type { Account, ShopCard, ShopProduct, ShopVariant } from "./types";
import type { Basket } from "./basket";
import { Go, type GoFn } from "./route";
import { Arrow, freeDeliveryRule, get, rands } from "./shared";
import { Pic } from "./img";
import { deptRoute } from "./nav";
import { setSeo } from "./seo";
import { track } from "./track";
import { ShareButton } from "./ShareButton";
import { CollectLink } from "./collect";
import { IconChevronRight, IconMinus, IconPlus, IconReturn, IconShield, IconTruck } from "./icons";
import { Countdown } from "./ui";
import { ProductCard } from "./Listing";
import { Strip } from "./Home";
import { ProductReviewsSection, RatingLink, useProductReviews } from "./ProductReviews";
import { SaveWithBundle } from "./bundles-ui";

interface Strips {
  related: ShopCard[];
  alsoBought: ShopCard[];
  /** True when the second strip holds no real co-purchase — titled "You might also like". */
  alsoBoughtSuggested: boolean;
}
const NO_STRIPS: Strips = { related: [], alsoBought: [], alsoBoughtSuggested: true };

export function ProductPage({ handle, go, basket, account, onAdded, freeDeliveryOverCents = null }: { handle: string; go: GoFn; basket: Basket; account: Account | null; onAdded: () => void; freeDeliveryOverCents?: number | null }) {
  const [p, setP] = useState<ShopProduct | null>(null);
  const [busy, setBusy] = useState(true);
  const [shot, setShot] = useState(0);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const [more, setMore] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [strips, setStrips] = useState<Strips>(NO_STRIPS);
  const mainBtn = useRef<HTMLButtonElement | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  const trade = account?.list === "wholesale";
  /** "Customers also bought" is a claim: only when at least one card is a real co-purchase (shop-related.ts). */
  const alsoTitle = strips.alsoBoughtSuggested ? "You might also like" : "Customers also bought";
  const reviews = useReviews();
  const pr = useProductReviews(handle);

  /** The two strips under the description. Failure or empty = no strip; the page never waits on them. */
  useEffect(() => {
    const ac = new AbortController();
    setStrips(NO_STRIPS);
    get<Partial<Strips>>(`/shop/product/${encodeURIComponent(handle)}/related`, ac.signal)
      .then((d) => setStrips({ related: d.related ?? [], alsoBought: d.alsoBought ?? [], alsoBoughtSuggested: d.alsoBoughtSuggested ?? false }))
      .catch(() => undefined);
    return () => ac.abort();
  }, [handle]);

  /** Bumped when a deal's countdown reaches zero, to re-read the price the server now charges. */
  const [reload, setReload] = useState("");
  useEffect(() => {
    const ac = new AbortController();
    if (!reload) { setBusy(true); setShot(0); setQty(1); setMore(false); }
    get<ShopProduct>(`/shop/product/${encodeURIComponent(handle)}${reload ? `?after=${encodeURIComponent(reload)}` : ""}`, ac.signal)
      .then((d) => {
        setP(d);
        if (reload) return;
        /** `?variant=<numeric id>` — the product feed's `link` (shop-feed.ts) lands on the variant it priced. */
        const asked = new URLSearchParams(window.location.search).get("variant");
        const linked = asked && /^\d+$/.test(asked) ? d.variants.find((v) => v.id === `gid://shopify/ProductVariant/${asked}`) : undefined;
        setVariantId(linked?.id ?? d.variants.find((v) => v.inStock)?.id ?? d.variants[0]?.id ?? null);
      })
      .catch(() => { if (!ac.signal.aborted && !reload) setP(null); })
      .finally(() => { if (!ac.signal.aborted) setBusy(false); });
    return () => ac.abort();
  }, [handle, reload]);

  /**
   * One view beacon per product page view, for the daily-deal picker's "site interest". It
   * carries the product handle and nothing else — no id, no cookie (`credentials: "omit"`).
   */
  const viewed = useRef("");
  useEffect(() => {
    if (!p || p.handle !== handle || viewed.current === handle) return;
    viewed.current = handle;
    const v = p.variants.find((x) => x.id === variantId) ?? p.variants.find((x) => x.inStock) ?? p.variants[0];
    track("view_item", { handle, contentName: p.title, valueCents: v?.priceCents ?? p.priceCents ?? undefined, contentIds: v?.feedId ? [v.feedId] : undefined });
  }, [handle, p, variantId]);

  useEffect(() => {
    if (p) setSeo(p.title, `${p.title}${p.shopBrand || p.vendor ? ` by ${p.shopBrand || p.vendor}` : ""} — ${rands(p.priceCents)} at Z S MERCH. Delivered in 2–7 working days.`);
  }, [p]);

  /** The sticky add-to-cart bar appears once the real button has scrolled off the top of the screen. */
  useEffect(() => {
    const el = mainBtn.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setStuck(!!e && !e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, [p]);

  if (busy) {
    return <div className="wrap pdp"><div className="skel sq" /><div><div className="skel line1" /><div className="skel line2" /></div></div>;
  }
  if (!p) return <div className="empty"><h1>We could not find that product</h1><Go to={{ name: "home" }} go={go} className="btn btn-quiet">Back to the shop</Go></div>;

  const variant = p.variants.find((v) => v.id === variantId) ?? p.variants[0] ?? null;
  const price = variant?.priceCents ?? p.priceCents;
  const inStock = variant ? variant.inStock : p.inStock;
  const images = p.images.length > 0 ? p.images : p.image ? [{ url: p.image, alt: p.imageAlt, width: null, height: null }] : [];
  const was = variant && variant.wasCents !== undefined ? variant.wasCents : p.wasCents;
  const saving = was && price && was > price ? was - price : 0;
  const multi = p.variants.length > 1;
  /** The deal on THIS variant (the server only reports one it is actually applying). */
  const deal = variant ? (variant.deal ?? null) : (p.deal ?? null);
  const maxQty = deal?.maxPerOrder ?? 500;

  const addToBasket = (): boolean => {
    if (!variant || !inStock) return false;
    basket.add({
      variantId: variant.id, handle: p.handle, title: p.title,
      variantTitle: variant.title && variant.title !== "Default Title" ? variant.title : "",
      sku: variant.sku, image: p.image, priceCents: price, ...(variant.feedId ? { feedId: variant.feedId } : {}),
    }, Math.min(qty, maxQty));
    track("add_to_cart", { handle: p.handle, variantId: variant.id, quantity: Math.min(qty, maxQty), valueCents: price, contentName: p.title, contentIds: variant.feedId ? [variant.feedId] : undefined });
    return true;
  };
  const onScroll = () => {
    const s = strip.current;
    if (s && s.clientWidth > 0) setShot(Math.round(s.scrollLeft / s.clientWidth));
  };
  const goShot = (i: number) => {
    setShot(i);
    const s = strip.current;
    if (s) s.scrollTo({ left: i * s.clientWidth, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  /** Choosing a variant shows its own photo when it has one (always one of the gallery's). */
  const pickVariant = (v: ShopVariant) => {
    setVariantId(v.id);
    const i = v.image ? images.findIndex((im) => im.url === v.image) : -1;
    if (i >= 0) goShot(i);
  };

  return (
    <div className="wrap pdp-page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <ol>
          <li><Go to={{ name: "home" }} go={go}>Home</Go><IconChevronRight size={12} /></li>
          {(p.breadcrumbs ?? []).map((b) => <li key={b.id}><Go to={deptRoute(b)} go={go}>{b.name}</Go><IconChevronRight size={12} /></li>)}
          <li><span aria-current="page">{p.title}</span></li>
        </ol>
      </nav>
      <div className="pdp">
        <div className="gallery">
          <div className="gal-strip" ref={strip} onScroll={onScroll} tabIndex={0} aria-label={`${p.title} photos`}>
            {images.length === 0 && <div className="gal-slide"><div className="skel fill" /></div>}
            {images.map((im, i) => (
              <div className="gal-slide zoom" key={im.url + i}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--zx", `${((e.clientX - r.left) / r.width) * 100}%`);
                  e.currentTarget.style.setProperty("--zy", `${((e.clientY - r.top) / r.height) * 100}%`);
                }}>
                <Pic src={im.url} alt={im.alt ?? p.title} sizes="(min-width: 900px) 48vw, 100vw" width={im.width ?? 900} height={im.height ?? 900} eager={i === 0} />
              </div>
            ))}
          </div>
          {images.length > 1 && (
            <>
              <div className="gal-dots" aria-hidden="true">{images.map((im, i) => <i key={im.url + i} className={i === shot ? "on" : undefined} />)}</div>
              <div className="thumbs">{images.map((im, i) => (
                <button type="button" key={im.url + i} aria-pressed={i === shot} onClick={() => goShot(i)} aria-label={`View photo ${i + 1} of ${images.length}`}>
                  <Pic src={im.url} alt={`${p.title} - photo ${i + 1}`} sizes="72px" width={72} height={72} />
                </button>
              ))}</div>
            </>
          )}
        </div>

        <div className="buybox">
          <h1>{p.title}</h1>
          <p className="meta">{p.shopBrand ? <span>Brand: {p.shopBrand}</span> : p.vendor && <span>{p.vendor}</span>}{(p.shopBrand || p.vendor) && variant?.sku && " · "}{variant?.sku && <span className="money">SKU {variant.sku}</span>}</p>
          <RatingLink summary={pr.summary} />
          <ShareButton handle={p.handle} title={p.title} />

          {multi && (
            <fieldset className="opts">
              <legend>{p.options[0]?.name ?? "Option"}: <b>{variant?.title}</b></legend>
              <div className="opt-btns">
                {p.variants.map((v) => (
                  <button type="button" key={v.id} className={`opt${v.id === variant?.id ? " on" : ""}${v.inStock ? "" : " out"}`} aria-pressed={v.id === variant?.id} onClick={() => pickVariant(v)}>
                    {v.title}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          <div className="row-p">
            <span className="lbl">Price</span>
            <span className="price money"><b className={saving > 0 ? "sale" : undefined}>{rands(price)}</b>{saving > 0 && <s>{rands(was)}</s>}{saving > 0 && <span className="save-flag inline">Save {rands(saving)}</span>}</span>
            {trade && price !== null && <span className="trade-tag">{deal?.kind === "clearance" && saving > 0 ? "Clearance price" : "Wholesale price"}</span>}
          </div>
          {deal?.kind === "daily" && deal.endsAt && (
            <div className="pdp-deal">
              <span className="deal-flag day static">Deal of the day</span>
              <span>Ends in <Countdown endsAt={deal.endsAt} onEnd={() => setReload(deal.endsAt!)} /></span>
              {deal.maxPerOrder && <span className="deal-limit">Limit {deal.maxPerOrder} per order</span>}
            </div>
          )}
          {deal?.kind === "clearance" && (
            <div className="pdp-deal"><span className="deal-flag clr static">Clearance</span><span>{trade ? "Last units — you pay whichever is lower, your wholesale price or the clearance price." : "Marked down until it is gone."}</span></div>
          )}
          {trade && <div className="price-note">{deal?.kind === "clearance" && saving > 0 ? "Clearance price — lower than your wholesale price" : "Your wholesale price"}</div>}
          <div className="row-p">
            <span className="lbl">Stock</span>
            <span className={`stock stock-lg${inStock ? "" : " out"}`}><i aria-hidden="true" />{inStock ? "In stock, ready to ship" : "Sold out — we will confirm a date"}</span>
          </div>
          {/* Collect-in-store readiness (shop-collect.ts): a sentence and a map link, never a count. */}
          {inStock && variant?.collect && variant.collect.when !== "none" && (
            <div className="row-p"><span className="lbl">Collect</span><CollectLink label={variant.collect.label} mapUrl={p.collectPoint?.mapUrl} /></div>
          )}
          {/* Free delivery on retail orders of R2,000 or more (owner, 2026-10-30): retail only — config sends null for wholesale. */}
          {!trade && freeDeliveryOverCents ? (
            <div className="row-p"><span className="lbl">Delivery</span><span>{freeDeliveryRule(freeDeliveryOverCents)}, where our courier delivers</span></div>
          ) : null}

          <div className="buy-actions">
            <div className="qty" role="group" aria-label="Quantity">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Fewer"><IconMinus /></button>
              <span aria-live="polite">{qty}</span>
              <button type="button" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} aria-label="More" disabled={qty >= maxQty}><IconPlus /></button>
            </div>
            <button ref={mainBtn} type="button" className="btn btn-action btn-lg" disabled={!inStock || price === null} onClick={() => { if (addToBasket()) onAdded(); }}>Add to cart</button>
            <button type="button" className="btn btn-dark btn-lg" disabled={!inStock || price === null} onClick={() => { if (addToBasket()) go({ name: "checkout" }); }}>Buy now <Arrow /></button>
          </div>

          {p.list === "retail" && account?.tradeStatus !== "pending" && (
            <div className="trade-note">Buying for a business? Wholesale customers see their own pricing across the whole shop. <Go to={{ name: "account", tab: "up" }} go={go}>Open a wholesale account</Go>.</div>
          )}
          {account?.tradeStatus === "pending" && <div className="trade-note">Wholesale application pending — retail prices shown.</div>}

          {/* Bundles & Kits that hold this product (shop-bundles.ts) — nothing when there are none. */}
          <SaveWithBundle handle={p.handle} go={go} />

          {/* "Read our guide": the buying guides for this product's department — nothing when there are none. */}
          {(p.guides ?? []).length > 0 && (
            <div className="pd-guides">
              <span className="pd-guides-kicker">Not sure which to buy?</span>
              {(p.guides ?? []).slice(0, 2).map((g) => (
                <Go key={g.slug} to={{ name: "guide", slug: g.slug }} go={go} className="pd-guide-link">
                  Read our guide: {g.title} <span aria-hidden="true">→</span>
                </Go>
              ))}
            </div>
          )}

          <ul className="trust">
            <li><IconTruck size={22} /><span><b>Delivery in 2–3 working days to main centres, 3–5 to outlying &amp; regional areas</b>, or collect at {p.collectPoint?.name ?? "our shop"}</span></li>
            <li><IconReturn size={22} /><span><b>14-day returns</b> on unused goods in original packaging</span></li>
            <li><IconShield size={22} /><span><b>Secure payment</b> by card or instant EFT</span></li>
            {reviews && <li><IconShield size={22} /><span><ReviewBadge r={reviews} /></span></li>}
          </ul>
        </div>
      </div>

      {p.descriptionHtml && (
        <section className="desc" aria-label="Description">
          <h2>Description</h2>
          <div className={`prose${more ? "" : " clamped"}`}
            /**
             * ⚠️ The description is the shop's OWN copy, written by the owner in Shopify's editor,
             * and it is stored and served by us. It is not visitor input and there is no path by
             * which a stranger can write it — which is the only reason this is acceptable here.
             */
            dangerouslySetInnerHTML={{ __html: p.descriptionHtml }} />
          <button type="button" className="more-btn" aria-expanded={more} onClick={() => setMore((m) => !m)}>{more ? "− View less" : "+ View more"}</button>
        </section>
      )}

      <ProductReviewsSection rv={pr} />

      {strips.related.length > 0 && (
        <section className="pdp-strip" aria-label="Related products">
          <h2>Related products</h2>
          <Strip label="Related products">{strips.related.map((c, i) => <div role="listitem" key={c.handle}><ProductCard p={c} i={i + 4} go={go} trade={trade} /></div>)}</Strip>
        </section>
      )}
      {strips.alsoBought.length > 0 && (
        <section className="pdp-strip" aria-label={alsoTitle}>
          <h2>{alsoTitle}</h2>
          <Strip label={alsoTitle}>{strips.alsoBought.map((c, i) => <div role="listitem" key={c.handle}><ProductCard p={c} i={i + 4} go={go} trade={trade} /></div>)}</Strip>
        </section>
      )}

      <div className={`sticky-buy${stuck ? " show" : ""}`} aria-hidden={!stuck}>
        <div className="sticky-info"><b className="money">{rands(price)}</b><span>{p.title}</span></div>
        <button type="button" className="btn btn-action" tabIndex={stuck ? 0 : -1} disabled={!inStock || price === null} onClick={() => { if (addToBasket()) onAdded(); }}>
          {inStock ? "Add to cart" : "Sold out"}
        </button>
      </div>
    </div>
  );
}
